import type { ReactElement } from 'react';
import React, { forwardRef } from 'react';
import { FormControlLabel as MuiFormControlLabel } from '@mui/material';
import { isEmpty } from 'lodash';

import { reportErrorInProductionOrThrow } from '~/Utils';

/**
 This Component is a wrapper on MUI component.
 This component should be imported and not MUI directly!

 Explicit props based on actual usage in the codebase.
**/
export interface FormControlLabelProps {
  /** A control element, for instance a Checkbox, Radio or Switch. */
  control: ReactElement;
  /** A text or an element to be used in an enclosing label element. Omitted by controls that
   * carry their label elsewhere, e.g. a switch in a two-column layout. */
  label?: string | number | ReactElement;
  /** The value of the component (used when nested inside a RadioGroup). */
  value?: unknown;
  /** The position of the label relative to the control. */
  labelPlacement?: 'bottom' | 'end' | 'start' | 'top';
  /** If true, the control is disabled. */
  disabled?: boolean;
  /** Inline styles applied to the root element. */
  style?: React.CSSProperties;
  /** CSS class name applied to the root element. */
  className?: string;
}

const FormControlLabel = forwardRef<HTMLLabelElement, FormControlLabelProps>(
  ({ control, label, value, labelPlacement, disabled, style, className, ...restProps }, ref) => {
    if (!isEmpty(restProps)) {
      reportErrorInProductionOrThrow(
        `Unexpected props passed to FormControlLabel: ${Object.keys(restProps).join(', ')}.
Examine if these need to be removed, or added explicitly to the FormControlLabelProps interface and to the component's signature.`,
        true
      );
    }

    return (
      <MuiFormControlLabel
        ref={ref}
        control={control}
        // MUI v5 reads `label.type` before any guard, so a nullish label throws rather than
        // rendering nothing. `''` is what MUI v4 rendered for the same input, which keeps the
        // label-less consumers rendering exactly as they did before the v5 migration.
        label={label ?? ''}
        value={value}
        labelPlacement={labelPlacement}
        disabled={disabled}
        style={style}
        className={className}
        {...restProps}
      />
    );
  }
);

FormControlLabel.displayName = 'FormControlLabel';

export default FormControlLabel;
